import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  ResetButton,
  MessageBox,
  Button,
  CustomConfirmButton,
} from '../../../components/FormElements';
import FiltersButton from '../../../components/FiltersButton/FiltersButton';
import { StyledLink, StyledFiltersContainer } from '../../../components/Styled';
import API from '../../../api';
import { API_RESPONSE_TYPES, ACCOUNT_TYPES } from '../../../constants';
import { getSearchParams, authData } from '../../../utils';

// Highlight search term
const highlightText = (text, search) => {
  if (!search) return text;
  const regex = new RegExp(`(${search})`, 'gi');
  return text?.toString().split(regex).map((part, index) =>
    regex.test(part) ? (
      <mark
        key={index}
        style={{
          background: '#ffeb3b',
          padding: '2px 4px',
          borderRadius: '3px',
          animation: 'highlightFade 1s ease',
          color: '#000',
        }}
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Predefined gradients
const GRADIENTS = [
  'linear-gradient(135deg, #f0f0f0, #a0a0a0)',
  // Add more if needed
];

const getCardGradient = (index) => GRADIENTS[index % GRADIENTS.length];

const getIconByType = (type) => {
  switch (type?.toLowerCase()) {
    case 'pay as you go':
      return '💳';
    case 'fixed':
      return '📦';
    default:
      return '❔';
  }
};

const PackagesRead = (props) => {
  const { accountType } = authData.get();
  const navigate = useNavigate(); // ✅ useNavigate inside component

  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [packageName, setPackageName] = useState('');
  const [packages, setPackages] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const readData = useCallback(
    (packageName = '') => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/packages/?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&packageName=${packageName}`
      )
        .then((response) => {
          const { status, message, data, totalRecords } = response.data;
          if (status === API_RESPONSE_TYPES.FAILURE) {
            setResponseStatus(status);
            setResponseMessage(message);
          } else {
            setTotalRecords(totalRecords);
            setPackages(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => setIsLoading(false));
    },
    [paginationData]
  );

  const deleteData = (id) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.delete(`/packages/${id}`)
      .then((response) => {
        const { status, message } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          reloadData();
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => setIsLoading(false));
  };

  const reloadData = () => readData(packageName);
  const resetFilters = () => {
    setPackageName('');
    readData();
  };
  const filterData = (e) => {
    e.preventDefault();
    readData(packageName);
  };

  useEffect(() => {
    readData();
  }, [paginationData, readData]);

  return (
    <>
      <PageHeading
        {...props}
        canCreate={true}
        createLink={'create'}
        createTitle="Create Package"
        canReload={true}
        onClickReload={reloadData}
      />

      <FiltersButton onClick={() => setShowFilters(!showFilters)} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Package Name</Label>
            <TextInput
              value={packageName}
              onChange={setPackageName}
              placeholder="Search package name"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">Filter</Button>
            <ResetButton disabled={isLoading} type="button" onClick={resetFilters}>Clear</ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>

      <LoadingSpinner isLoading={responseStatus === '' && packages.length === 0 && isLoading} />
      <NoData
        status={responseStatus !== API_RESPONSE_TYPES.FAILURE && !isLoading && packages.length === 0}
        message="No packages found"
      />
      {responseStatus === API_RESPONSE_TYPES.FAILURE && <MessageBox status={responseStatus} message={responseMessage} />}

      {packages.length > 0 && (
        <div className="cards-container">
          {packages.map((item, index) => (
            <div key={item.id} className="package-card" style={{ background: getCardGradient(index) }}>
              <div className="card-header">
                <div className="package-icon">{getIconByType(item.packageType)}</div>
                <div className="package-name">{highlightText(item.packageName, packageName)}</div>
                <div className="card-actions">
                  {!ACCOUNT_TYPES.API_USERS.includes(accountType) && (
                    <>
                      <StyledLink to={`update/${item.id}`} title="Update Package">
                        <FontAwesomeIcon icon={faEdit} />
                      </StyledLink>
                      <CustomConfirmButton onClick={() => deleteData(item.id)} />
                    </>
                  )}
                </div>
              </div>
              <div className="card-body">
                <div className="card-field"><strong>Package Type:</strong> {highlightText(item.packageType, packageName)}</div>
                <div className="card-field"><strong>Description:</strong> {highlightText(item.description, packageName)}</div>
                <div className="card-field"><strong>Price:</strong> {highlightText(item.price, packageName)}</div>
                <div className="card-field"><strong>Charge Per Hit:</strong> {highlightText(item.chargePerHit, packageName)}</div>
                <div className="card-field"><strong>Duration:</strong> {item.duration ? `${item.duration} Days` : 'N/A'}</div>
              </div>
              {ACCOUNT_TYPES.API_USERS.includes(accountType) && (
                <div className="card-footer">
                  <Button
                    style={{ width: '80%', margin: '0 auto', fontSize: '16px', padding: '10px 0' }}
                    onClick={() => {
                      if (!item.price) return;
                      sessionStorage.setItem(
                        'paymentData',
                        JSON.stringify({ amount: item.price, remark: item.packageName,packageid: item.id })
                      );
                      navigate('/accounting/payment-gateway-deposits/create');
                    }}
                  >
                    Buy
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes highlightFade {
          0% { background-color: #fff176; box-shadow: 0 0 4px rgba(255,235,59,0.9); }
          50% { background-color: #ffeb3b; }
          100% { background-color: #fff59d; }
        }
        .cards-container { display: grid; gap: 18px; margin-top: 20px; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }
        .package-card { display: flex; flex-direction: column; justify-content: space-between; border-radius: 8px; overflow: hidden; box-shadow: 0 3px 10px rgba(0,0,0,0.08); transition: transform 0.2s ease; min-height: 360px; color: #000; }
        .package-card:hover { transform: translateY(-2px); }
        .card-header { display: flex; align-items: center; padding: 16px; background: rgba(0,0,0,0.15); gap: 8px; }
        .package-icon { font-size: 32px; width: 40px; flex-shrink: 0; }
        .package-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; font-weight: bold; font-size: 18px; }
        .card-actions { display: flex; gap: 12px; }
        .card-body { flex: 1; padding: 16px; }
        .card-field { margin-bottom: 10px; }
        .card-footer { padding: 16px; display: flex; justify-content: center; }
        mark { color: #000; }
      `}</style>
    </>
  );
};

export default withAppHOC(PackagesRead);
